'use client';

import { RefObject, useEffect, useState } from 'react';

interface Args extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

export function useIntersectionObserver(
  elementRef: RefObject<Element>,
  { freezeOnceVisible = false, root = null, rootMargin = '0%', threshold = 0 }: Args,
  onUpdate?: (entry: IntersectionObserverEntry) => void,
): IntersectionObserverEntry | undefined {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = ([entry]: IntersectionObserverEntry[]): void => {
    setEntry(entry);
    onUpdate && onUpdate(entry);
  };

  useEffect(() => {
    const node = elementRef?.current;
    const hasIOSupport = typeof window.IntersectionObserver === 'function';

    if (!node) return;

    if (!hasIOSupport) {
      setEntry({ isIntersecting: true } as IntersectionObserverEntry);
      return;
    }

    if (frozen) return;

    const observerParams = { root, rootMargin, threshold };
    const observer = new IntersectionObserver(updateEntry, observerParams);

    observer.observe(node);

    return () => observer.disconnect();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef?.current, JSON.stringify(threshold), root, rootMargin, frozen]);

  return entry;
}
